import React, { useState } from 'react';
import { Box, TextField, Popover, MenuItem } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { dashboardStyles } from '../../../styles/dashboard';

interface DateRangePickerProps {
  startDate: Dayjs;
  endDate: Dayjs;
  onStartDateChange: (date: Dayjs | null) => void;
  onEndDateChange: (date: Dayjs | null) => void;
}

const years = [2024, 2025, 2026, 2027];
const months = Array.from({ length: 12 }, (_, i) => i + 1);

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange
}) => {
  // 시작월
  const [startAnchor, setStartAnchor] = useState<null | HTMLElement>(null);
  const [startYear, setStartYear] = useState(startDate.year());
  const [startMonth, setStartMonth] = useState(startDate.month() + 1);
  // 종료월
  const [endAnchor, setEndAnchor] = useState<null | HTMLElement>(null);
  const [endYear, setEndYear] = useState(endDate.year());
  const [endMonth, setEndMonth] = useState(endDate.month() + 1);

  // 연도 선택 시 월 선택 대기
  const handleStartYearClick = (e: React.MouseEvent<HTMLElement>) => {
    setStartAnchor(e.currentTarget);
  };
  const handleEndYearClick = (e: React.MouseEvent<HTMLElement>) => {
    setEndAnchor(e.currentTarget);
  };
  const handleStartYearSelect = (year: number) => {
    setStartYear(year);
  };
  const handleEndYearSelect = (year: number) => {
    setEndYear(year);
  };
  const handleStartMonthSelect = (month: number) => {
    setStartMonth(month);
    setStartAnchor(null);
    onStartDateChange(dayjs(`${startYear}-${String(month).padStart(2, '0')}-01`));
  };
  const handleEndMonthSelect = (month: number) => {
    setEndMonth(month);
    setEndAnchor(null);
    onEndDateChange(dayjs(`${endYear}-${String(month).padStart(2, '0')}-01`));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
      <Box sx={{ display: 'flex', gap: 1 }}>
        {/* 시작월 */}
        <TextField
          label="시작월"
          value={`${startYear}년 ${startMonth}월`}
          onClick={handleStartYearClick}
          size="small"
          sx={{
            width: 140,
            bgcolor: '#fff',
            borderRadius: 1,
            fontWeight: 700,
            fontSize: 16,
            input: { color: '#222', fontWeight: 700, fontSize: 16, background: '#fff' },
            label: { color: '#222', fontWeight: 700, fontSize: 18, background: '#fff' },
            '& .MuiInputLabel-root': {
              color: '#222',
              fontWeight: 700,
              fontSize: 18,
              background: '#fff',
            },
            '& .MuiOutlinedInput-root': {
              background: '#fff',
              borderRadius: 1,
              color: '#222',
              fontWeight: 700,
              fontSize: 16,
              '& fieldset': {
                borderColor: '#ffc107',
                borderWidth: 2
              },
              '&:hover fieldset': {
                borderColor: '#ff4444',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#ff4444',
                borderWidth: 2
              },
            },
          }}
          InputProps={{ readOnly: true }}
        />
        <Popover
          open={Boolean(startAnchor)}
          anchorEl={startAnchor}
          onClose={() => setStartAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', p: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              {years.map(year => (
                <MenuItem key={year} selected={year === startYear} onClick={() => handleStartYearSelect(year)}>{year}년</MenuItem>
              ))}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {months.map(month => (
                <MenuItem key={month} selected={month === startMonth} onClick={() => handleStartMonthSelect(month)}>{month}월</MenuItem>
              ))}
            </Box>
          </Box>
        </Popover>
        {/* 종료월 */}
        <TextField
          label="종료월"
          value={`${endYear}년 ${endMonth}월`}
          onClick={handleEndYearClick}
          size="small"
          sx={{
            width: 140,
            bgcolor: '#fff',
            borderRadius: 1,
            fontWeight: 700,
            fontSize: 16,
            input: { color: '#222', fontWeight: 700, fontSize: 16, background: '#fff' },
            label: { color: '#222', fontWeight: 700, fontSize: 18, background: '#fff' },
            '& .MuiInputLabel-root': {
              color: '#222',
              fontWeight: 700,
              fontSize: 18,
              background: '#fff',
            },
            '& .MuiOutlinedInput-root': {
              background: '#fff',
              borderRadius: 1,
              color: '#222',
              fontWeight: 700,
              fontSize: 16,
              '& fieldset': {
                borderColor: '#ffc107',
                borderWidth: 2
              },
              '&:hover fieldset': {
                borderColor: '#ff4444',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#ff4444',
                borderWidth: 2
              },
            },
          }}
          InputProps={{ readOnly: true }}
        />
        <Popover
          open={Boolean(endAnchor)}
          anchorEl={endAnchor}
          onClose={() => setEndAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', p: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              {years.map(year => (
                <MenuItem key={year} selected={year === endYear} onClick={() => handleEndYearSelect(year)}>{year}년</MenuItem>
              ))}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {months.map(month => (
                <MenuItem key={month} selected={month === endMonth} onClick={() => handleEndMonthSelect(month)}>{month}월</MenuItem>
              ))}
            </Box>
          </Box>
        </Popover>
      </Box>
    </LocalizationProvider>
  );
};

export default DateRangePicker; 